import React, { ChangeEvent, FC, Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    Grid,
    Paper,
    TextField,
    Typography,
} from '@mui/material';
import OtherInfoForm from './forms/other-info-form.component';
import { useFormik } from 'formik';
import MainInfoForm from './forms/main-info-form.component';
import AddressForm from 'components/shared/address/address-form.component';
import { Address } from 'models/contact';
import { ClinicFormData, ClinicMainInfoFormData, ClinicOtherInfoFormData } from 'models/forms';
import { emptyClinicFormData } from './clinic-form-helper';
import { Clinic } from 'models/clinic';
import LocaleFormatPicker from 'components/shared/locale-format-picker/locale-format-picker.component';
import { DEFAULT_LOCALE_FORMAT, LocaleFormat } from 'models/locale-format';

type ClinicFormProps = {
    clinic: Partial<Clinic>;
    onClose: () => void;
    onFormSubmitted: (formData: ClinicFormData) => void;
    submitButtonText: string;
    allowFullEdit?: boolean;
    showDentistReminder?: boolean;
    hideExtendedFields?: boolean;
    isLocalClinic?: boolean;
    defaultCountryCode?: string;
};
const ClinicForm: FC<ClinicFormProps> = ({
    clinic,
    onClose,
    onFormSubmitted,
    submitButtonText,
    allowFullEdit,
    showDentistReminder,
    hideExtendedFields,
    isLocalClinic,
    defaultCountryCode: initialDefaultCountryCode,
}: ClinicFormProps) => {
    const { t: translate } = useTranslation(['common', 'organization', 'client-directory']);
    const [isWhiteLabeled, setIsWhiteLabeled] = useState(false);
    const [isPickUpAndDeliveryPlace, setIsPickUpAndDeliveryPlace] = useState(true);
    const [isDifferentInvoiceAddress, setIsDifferentInvoiceAddress] = useState(false);
    const [isDifferentPostalAddress, setIsDifferentPostalAddress] = useState(false);
    const [useExternalInfo, setUseExternalInfo] = useState(false);
    const [isOtherInfoValid, setIsOtherInfoValid] = useState(true);
    const [isMainFormValid, setIsMainFormValid] = useState(false);
    const [defaultCountryCode, setDefaultCountryCode] = useState<string>(initialDefaultCountryCode ?? 'SE');

    const validate = (formData: ClinicFormData) => {
        const errors: Record<string, string> = {};
        if (!isMainFormValid) {
            errors.forms = 'MAIN_FORM_NOT_VALID';
        }
        if (!isOtherInfoValid) {
            errors.forms = 'OTHER_FORM_NOT_VALID';
        }
        if (isDifferentInvoiceAddress && !formData.invoicing?.invoiceAddress) {
            errors.invoiceAddress = 'INVOICE_FORM_NOT_VALID';
        }
        if (isDifferentPostalAddress && !formData.contact?.postalAddress) {
            errors.invoiceAddress = 'POSTAL_FORM_NOT_VALID';
        }
        return errors;
    };

    const formik = useFormik<ClinicFormData>({
        initialValues: emptyClinicFormData(),
        validate,
        onSubmit: async (clinicForm) => {
            if (formik.isValid) {
                onFormSubmitted(clinicForm);
            }
        },
    });

    useEffect(() => {
        if (clinic) {
            formik.setValues(clinic as ClinicFormData);
            if (clinic.contact?.address.country) {
                setDefaultCountryCode(clinic.contact?.address.country);
            }
            setIsDifferentInvoiceAddress(!!clinic.invoicing?.invoiceAddress);
            setIsDifferentPostalAddress(!!clinic.contact?.postalAddress);
            setUseExternalInfo(!!clinic.externalInfo?.externalId1 || !!clinic.externalInfo?.externalId1);
            setIsPickUpAndDeliveryPlace(!clinic.isNotPickUpAndDeliveryPlace);
            setIsWhiteLabeled(clinic.isWhiteLabeled || false);
        }
    }, [clinic]);

    const handleMainInfoFormChange = (formData: ClinicMainInfoFormData, isFormValid: boolean) => {
        formik.setFieldValue('name', formData.name, false);
        formik.setFieldValue('officialName', formData.officialName, false);
        formik.setFieldValue('organizationNumber', formData.organizationNumber, false);
        formik.setFieldValue('contact.address', formData.address, false);
        formik.setFieldValue('ptjCustomerNumber', formData.ptjCustomerNumber, false);
        formik.setFieldValue('crediflowPartyId', formData.crediflowPartyId, false);
        if (isLocalClinic) {
            formik.setFieldValue('localLabOwner', formData.localLabOwner, false);
        }
        setIsMainFormValid(isFormValid);
        setTimeout(() => formik.validateForm(), 0);
    };

    const handleOtherInfoChange = (formData: ClinicOtherInfoFormData, isFormValid: boolean) => {
        formik.setFieldValue('invoicing.vatNo', formData.vatNo, false);
        formik.setFieldValue('invoicing.costCenter', formData.costCenter, false);
        formik.setFieldValue('invoicing.communication.email', formData.invoiceEmail, false);
        formik.setFieldValue('gln', formData.gln, false);
        formik.setFieldValue('contact.communication.email', formData.email, false);
        formik.setFieldValue('contact.communication.phone', formData.phone, false);
        formik.setFieldValue('contact.communication.site', formData.site, false);

        setIsOtherInfoValid(isFormValid);
        setTimeout(() => formik.validateForm(), 0);
    };

    const handlePickupAndDeliveryPlaceChange = (event: ChangeEvent<HTMLInputElement>) => {
        setIsPickUpAndDeliveryPlace(event.target.checked);
        formik.setFieldValue('isNotPickUpAndDeliveryPlace', !event.target.checked);
    };

    const handleIsWhiteLabeledChange = (event: ChangeEvent<HTMLInputElement>) => {
        setIsWhiteLabeled(event.target.checked);
        formik.setFieldValue('isWhiteLabeled', event.target.checked);
    };

    const handleDiffrentInvoiceAddressCheckbox = (checked: boolean) => {
        setIsDifferentInvoiceAddress(checked);
        if (!checked) {
            formik.setFieldValue('invoicing.invoiceAddress', undefined);
        }
        setTimeout(() => {
            formik.validateForm();
        });
    };

    const handleDiffrentPostalAddressCheckbox = (checked: boolean) => {
        setIsDifferentPostalAddress(checked);
        if (!checked) {
            formik.setFieldValue('contact.postalAddress', undefined);
        }
        setTimeout(() => {
            formik.validateForm();
        });
    };

    const handleInvoiceAddressChange = (address: Address | undefined) => {
        formik.setFieldValue('invoicing.invoiceAddress', address);
    };

    const handlePostalAddressChange = (address: Address | undefined) => {
        formik.setFieldValue('contact.postalAddress', address);
    };

    function handleUseExternalInfoCheckbox(checked: boolean): void {
        setUseExternalInfo(checked);
        if (!checked) {
            formik.setFieldValue('externalInfo.externalId1', '');
            formik.setFieldValue('externalInfo.externalId2', '');
        }
    }

    return (
        <form onSubmit={formik.handleSubmit}>
            <Box>
                {showDentistReminder ? (
                    <Box mb={3}>
                        <Alert severity="info">{translate('client-directory:ADD_DENTIST_REMINDER')}</Alert>
                    </Box>
                ) : undefined}

                {formik.values ? (
                    <Fragment>
                        <Box mt={2} mb={4}>
                            <MainInfoForm
                                clinic={clinic}
                                onChange={handleMainInfoFormChange}
                                allowFullEdit={allowFullEdit}
                                isLocalClinic={isLocalClinic}
                                defaultCountryCode={defaultCountryCode}
                            />
                        </Box>
                        <Box mb={3}>
                            <Typography variant="h4" mb={1.5}>
                                {translate('organization:OTHER_INFO')}
                            </Typography>
                            <OtherInfoForm clinic={clinic} onChange={handleOtherInfoChange} />
                        </Box>
                        <Box mb={3}>
                            <Typography variant="h4" mb={1.5}>
                                {translate('organization:LOCALE_FORMAT')}
                            </Typography>
                            <LocaleFormatPicker
                                value={formik.values.localeFormat || DEFAULT_LOCALE_FORMAT}
                                onChange={(next: LocaleFormat) => formik.setFieldValue('localeFormat', next)}
                            />
                        </Box>
                        <Box mb={3}>
                            {allowFullEdit && !isLocalClinic && !clinic.local ? (
                                <FormControl fullWidth>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                id="white-labeled"
                                                color="secondary"
                                                checked={isWhiteLabeled}
                                                onChange={handleIsWhiteLabeledChange}
                                            />
                                        }
                                        label={translate('organization:IS_WHITE_LABEL_CLINIC')}
                                    />
                                </FormControl>
                            ) : undefined}
                            {!hideExtendedFields ? (
                                <FormControl fullWidth>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                id="pickup"
                                                color="secondary"
                                                checked={isPickUpAndDeliveryPlace}
                                                onChange={handlePickupAndDeliveryPlaceChange}
                                            />
                                        }
                                        label={translate('organization:PICKUP_AND_DELIVERY_PLACE')}
                                    />
                                </FormControl>
                            ) : undefined}
                            <FormControl fullWidth>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            id="pickup"
                                            color="secondary"
                                            checked={isDifferentInvoiceAddress}
                                            onChange={(e) => handleDiffrentInvoiceAddressCheckbox(e.target.checked)}
                                        />
                                    }
                                    label={translate('organization:USE_DIFFERENT_INVOICE_ADDRESS')}
                                />
                                {isDifferentInvoiceAddress ? (
                                    <Box mb={1}>
                                        <AddressForm
                                            address={clinic.invoicing?.invoiceAddress}
                                            onChange={handleInvoiceAddressChange}
                                            defaultCountryCode={
                                                clinic.invoicing?.invoiceAddress?.country || defaultCountryCode
                                            }
                                            title={translate('client-directory:INVOICE_ADDRESS')}
                                        />
                                    </Box>
                                ) : undefined}
                            </FormControl>
                            <FormControl fullWidth>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            id="pickup"
                                            color="secondary"
                                            checked={isDifferentPostalAddress}
                                            onChange={(e) => handleDiffrentPostalAddressCheckbox(e.target.checked)}
                                        />
                                    }
                                    label={translate('organization:USE_DIFFERENT_POSTAL_ADDRESS')}
                                />
                                {isDifferentPostalAddress ? (
                                    <Box mb={1}>
                                        <AddressForm
                                            address={clinic.contact?.postalAddress}
                                            onChange={handlePostalAddressChange}
                                            defaultCountryCode={
                                                clinic.contact?.postalAddress?.country || defaultCountryCode
                                            }
                                            title={translate('organization:POSTAL_ADDRESS')}
                                        />
                                    </Box>
                                ) : undefined}
                            </FormControl>
                            {!hideExtendedFields ? (
                                <FormControl fullWidth>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                id="pickup"
                                                color="secondary"
                                                checked={useExternalInfo}
                                                onChange={(e) => handleUseExternalInfoCheckbox(e.target.checked)}
                                            />
                                        }
                                        label={translate('organization:USE_EXTERNAL_INFO')}
                                    />
                                    {useExternalInfo ? (
                                        <Box>
                                            <Paper variant="outlined">
                                                <Box p={2}>
                                                    <Typography variant="h4" mb={1.5}>
                                                        {translate('organization:EXTERNAL_INFO')}
                                                    </Typography>
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={12} sm={4}>
                                                            <TextField
                                                                label={translate('organization:EXTERNAL_ID_1')}
                                                                fullWidth
                                                                {...formik.getFieldProps('externalInfo.externalId1')}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} sm={4}>
                                                            <TextField
                                                                label={translate('organization:EXTERNAL_ID_2')}
                                                                fullWidth
                                                                {...formik.getFieldProps('externalInfo.externalId2')}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            </Paper>
                                        </Box>
                                    ) : undefined}
                                </FormControl>
                            ) : undefined}
                        </Box>
                    </Fragment>
                ) : undefined}
                <Box display="flex">
                    <Box flexGrow={1}>
                        <Button onClick={() => onClose()}>{translate('common:CANCEL')}</Button>
                    </Box>
                    <Button variant="contained" type="submit" disabled={!formik.isValid}>
                        {submitButtonText}
                    </Button>
                </Box>
            </Box>
        </form>
    );
};

export default ClinicForm;
